import { call, put, takeEvery } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import BaiduConvertService from 'Map/utils/BadiuConvertService.js';

import {
    FETCH_ACTIVITY_DETAILS,
    FETCH_ACTIVITY_DETAILS_BAIDU,
    FETCH_ACTIVITY_DETAILS_START,
    FETCH_ACTIVITY_DETAILS_SUCCESS,
    FETCH_ACTIVITY_DETAILS_FAIL,
    FETCH_ACTIVITY_WEATHER,
    FETCH_ACTIVITY_WEATHER_SUCCESS,
    FETCH_ACTIVITY_WEATHER_FAIL,
    FETCH_ACTIVITY_DETAILS_METRICS_FOR_UPDATE,
    FETCH_ACTIVITY_DETAILS_BAIDU_METRICS_FOR_UPDATE,
    FETCH_ACTIVITY_DETAILS_METRICS_FOR_UPDATE_SUCCESS
} from 'actions/ActionTypes';
import { handlingSuccessCallback } from './sagasUtils';

function* fetchActivityDetailsSaga(action) {
    try {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_START
        });
        const maxChartSize = typeof action.maxChartSize === 'number' ? action.maxChartSize : 250;
        const maxPolylineSize = typeof action.maxPolylineSize === 'number' ? action.maxPolylineSize : 250;
        // the component calling this needs to support Activity Heatmap, or else the polyline data won't be returned if maxHeatMapSize is used
        const maxHeatMapSize = typeof action.maxHeatMapSize === 'number' ? action.maxHeatMapSize : null;
        const splitTypeFilter = action.splitTypeFilter
            ? `&splitType=${action.splitTypeFilter.join('&splitType=')}`
            : '';

        const detailsParams = {
            maxChartSize,
            maxPolylineSize
        };

        if (action.maxHeatMapSize) {
            detailsParams.maxHeatMapSize = maxHeatMapSize;
        }

        const response = yield call(
            gcApi.get,
            `/activity-service/activity/${action.activityId}/details?${splitTypeFilter}`,
            {
                params: detailsParams
            }
        );

        yield put({
            type: FETCH_ACTIVITY_DETAILS_SUCCESS,
            activityDetails: response.data
        });
        yield call(handlingSuccessCallback, action.callback, response);
    } catch (e) {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_FAIL,
            activityDetails: []
        });
        yield call(handlingSuccessCallback, action.callback, e);
    }
}

function* fetchActivityDetailsBaiduSaga(action) {
    try {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_START
        });
        const maxChartSize = typeof action.maxChartSize === 'number' ? action.maxChartSize : 250;
        const maxPolylineSize = typeof action.maxPolylineSize === 'number' ? action.maxPolylineSize : 250;
        const maxHeatMapSize = action.maxHeatMapSize;
        const response = yield call(gcApi.get, `/activity-service/activity/${action.activityId}/details`, {
            params: {
                maxChartSize,
                maxPolylineSize,
                maxHeatMapSize
            }
        });
        let activityHeatMapDTO = response.data?.activityHeatMapDTO || null;
        const polylinePoints = response.data?.geoPolylineDTO?.polyline || null;
        let convertService = new BaiduConvertService();

        if (activityHeatMapDTO) {
            let inputPoints = [
                { latitude: activityHeatMapDTO.lowerLeftLat || 0, longitude: activityHeatMapDTO.lowerLeftLon || 0 },
                { latitude: activityHeatMapDTO.upperRightLat || 0, longitude: activityHeatMapDTO.upperRightLon || 0 }
            ];
            const heatMapDTOPoints = response.data.activityHeatMapDTO.points || [];
            inputPoints = inputPoints.concat(heatMapDTOPoints);
            const convertResponse = yield convertService.fetchGenerator(inputPoints);
            const rect = convertResponse.slice(0, 2);
            const newDataPoints = convertResponse.slice(2);
            response.data.activityHeatMapDTO.lowerLeftLat = rect[0].lat;
            response.data.activityHeatMapDTO.lowerLeftLon = rect[0].lon;
            response.data.activityHeatMapDTO.upperRightLat = rect[1].lat;
            response.data.activityHeatMapDTO.upperRightLon = rect[1].lon;
            newDataPoints.forEach((convertedPoint, index) => {
                heatMapDTOPoints[index].lat = convertedPoint.lat;
                heatMapDTOPoints[index].lon = convertedPoint.lon;
            });
        } else if (polylinePoints) {
            // copy polyline to sourcePolyline for creating course from activity for China sice we need to save the original points
            if (action.fetchForCourse) {
                response.data.geoPolylineDTO.sourcePolyline = polylinePoints;
            }
            let convertedBaiduPoints = [];
            const convertResponse = yield convertService.fetchGenerator(polylinePoints);
            convertResponse.forEach((convertedPoint, index) => {
                convertedBaiduPoints.push({
                    lat: convertedPoint.lat,
                    lon: convertedPoint.lon,
                    time: polylinePoints[index].time
                });
            });
            response.data.geoPolylineDTO.polyline = convertedBaiduPoints;
        }

        yield put({
            type: FETCH_ACTIVITY_DETAILS_SUCCESS,
            activityDetails: response.data
        });
        yield call(handlingSuccessCallback, action.callback, response);
    } catch (e) {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_FAIL,
            activityDetails: []
        });
        yield call(handlingSuccessCallback, action.callback, e);
    }
}

function* fetchActivityDetailsMetricForUpdateSaga(action) {
    try {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_START
        });
        const maxChartSize = action.maxChartSize || 250;

        const response = yield call(gcApi.get, `/activity-service/activity/${action.activityId}/details`, {
            params: {
                maxChartSize,
                maxPolylineSize: 0
            }
        });

        yield put({
            type: FETCH_ACTIVITY_DETAILS_METRICS_FOR_UPDATE_SUCCESS,
            activityDetails: response.data
        });
        yield call(handlingSuccessCallback, action.callback, response);
    } catch (e) {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_FAIL,
            activityDetails: []
        });
    }
}

function* fetchActivityDetailsMetricBaiduForUpdateSaga(action) {
    try {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_START
        });
        const maxChartSize = action.maxChartSize || 250;
        const response = yield call(gcApi.get, `/activity-service/activity/${action.activityId}/details`, {
            params: {
                maxChartSize,
                maxPolylineSize: 0
            }
        });
        yield put({
            type: FETCH_ACTIVITY_DETAILS_METRICS_FOR_UPDATE_SUCCESS,
            activityDetails: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_ACTIVITY_DETAILS_FAIL,
            activityDetails: []
        });
    }
}

export function* fetchActivityWeatherSaga(action) {
    try {
        const response = yield call(gcApi.get, `/activity-service/activity/${action.activityId}/weather`);

        yield put({
            type: FETCH_ACTIVITY_WEATHER_SUCCESS,
            weather: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_ACTIVITY_WEATHER_FAIL,
            weather: []
        });
    }
}

export const activityDetailsSagas = [
    takeEvery(FETCH_ACTIVITY_DETAILS, fetchActivityDetailsSaga),
    takeEvery(FETCH_ACTIVITY_DETAILS_BAIDU, fetchActivityDetailsBaiduSaga),
    takeEvery(FETCH_ACTIVITY_WEATHER, fetchActivityWeatherSaga),
    takeEvery(FETCH_ACTIVITY_DETAILS_METRICS_FOR_UPDATE, fetchActivityDetailsMetricForUpdateSaga),
    takeEvery(FETCH_ACTIVITY_DETAILS_BAIDU_METRICS_FOR_UPDATE, fetchActivityDetailsMetricBaiduForUpdateSaga)
];
